import React, { useState, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import tw from 'twin.macro';
import { Modal } from '../modal';
import { RESEND_CONFIRMATION_CODE, SIGN_IN } from '../../services/authService/queries';
import { useMutation } from '@apollo/client';
import { toast } from 'react-toastify';
import {
  ISignInMutation,
  ISignInMutationVariables,
  IResendConfirmationCodeMutation,
  IResendConfirmationCodeMutationVariables,
} from '../../services/authService/__generated__/queries.generated';
import { useLocation } from 'react-router-dom';
import { authActions, authStore } from '../../../store/authStore';
import { Button } from '../button';
import { getBrand } from '../../constants/brands';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import verifyEmail from '../../../assets/images/verify_email.jpg';
import queryString from 'query-string';
import { modalActions, modalStore } from '../../../store/authStore/modalStore';
import { parseAuthEmail } from '../../constants/authRoutes';
import { useSnapshot } from 'valtio';
import { AppVersionLabel } from '../AppVersionLabel';
import { Colors } from '../../styles/colors';
import { validateSignInForm } from '../../utils/authFormValidation';
import {
  DividerContainer,
  DividerLine,
  DividerText,
  GoogleSignInButton,
} from './GoogleSignInButton';
import { showGoogleSignIn } from '../../../config/runtime-target';

const Container = styled.div`
  ${tw`w-full max-w-xl box-border`}
`;

const Title = styled.h2`
  ${tw`text-2xl md:text-3xl font-bold mb-6`}
`;

const FormGroup = styled.div`
  ${tw`mb-4`}
`;

const Label = styled.label`
  ${tw`block text-sm font-medium text-text-secondary mb-2`}
`;

const Input = styled.input<{ $invalid?: boolean }>`
  ${tw`w-[100%] border border-border-default border-solid rounded-md h-[44px] box-border px-3`}
  ${({ $invalid }) =>
    $invalid &&
    css`
      border-color: ${Colors.Error};
    `}
`;

const FieldError = styled.p`
  ${tw`text-sm mt-1 mb-0`}
  color: ${Colors.Error};
`;

const ForgotPasswordContainer = styled.div`
  ${tw`flex justify-between items-center text-sm mb-6`}
`;

const Link = styled.button`
  ${tw`text-black cursor-pointer border-none bg-transparent`}

  &:hover {
    ${tw`text-black`}
  }
`;

const Footer = styled.div`
  ${tw`mt-6 text-xs text-text-secondary text-center`}
`;

const TipBox = styled.div`
  ${tw`bg-bg-secondary p-4 rounded-lg flex items-start gap-3`}
`;

interface SignInModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SignInModal = ({ isOpen, onClose }: SignInModalProps) => {
  const { search } = useLocation();
  const { currentEmail } = useSnapshot(modalStore);
  const { isAuthenticated: storeAuthenticated, tokens: authTokens } = useSnapshot(authStore);
  const queryParams = queryString.parse(search);
  const decodedEmail = parseAuthEmail(queryParams.email) || currentEmail || '';
  const [email, setEmail] = useState(decodedEmail);
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState<string | undefined>();
  const [passwordError, setPasswordError] = useState<string | undefined>();
  const [showResendConfirmationCodePage, setShowResendConfirmationCodePage] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0); // Countdown in seconds
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasAutoSentRef = useRef(false); // Track if we've already auto-sent on error

  const isSendingRef = useRef(false);

  useEffect(() => {
    if (isOpen && decodedEmail) {
      setEmail(decodedEmail);
    }
  }, [decodedEmail, isOpen]);

  // Only auto-run the pending action when there's a real GraphQL session (store flag +
  // access token). OIDC `auth.isAuthenticated` can remain true after logout, which would
  // otherwise make this modal flash open and immediately close without a login chance.
  const hasValidSession = storeAuthenticated && !!authTokens?.accessToken;

  useEffect(() => {
    if (!isOpen || !hasValidSession) {
      return;
    }
    let isCancelled = false;
    const finishSignIn = async () => {
      await modalActions.executePendingAction();
      if (!isCancelled) {
        modalActions.closeSignInModal();
      }
    };
    finishSignIn();
    return () => {
      isCancelled = true;
    };
  }, [isOpen, hasValidSession]);

  const [resendConfirmationCode, { loading: resendConfirmationCodeLoading }] = useMutation<
    IResendConfirmationCodeMutation,
    IResendConfirmationCodeMutationVariables
  >(RESEND_CONFIRMATION_CODE, {
    onCompleted: data => {
      if (data?.resendConfirmationCode?.success) {
        toast.success('Verification email sent successfully!');
        setResendCooldown(60); // Start 60 second countdown
      }
      // Reset sending flag
      isSendingRef.current = false;
    },
    onError: error => {
      toast.error(error.message || 'Failed to resend verification email');
      // Reset sending flag on error
      isSendingRef.current = false;
    },
  });

  const [signIn, { loading }] = useMutation<ISignInMutation, ISignInMutationVariables>(SIGN_IN, {
    refetchQueries: ['GetUser'],
    onCompleted: async data => {
      if (data?.signIn) {
        authActions.setAuth(
          {
            accessToken: data.signIn.accessToken,
            idToken: data.signIn.idToken,
            refreshToken: data.signIn.refreshToken,
          },
          data.signIn.user,
          'EMAIL_SIGNIN',
        );

        toast.success('Login successful!', { autoClose: 2000 });

        // Execute pending action (e.g., retry createBookingIntent) then close modal
        setTimeout(async () => {
          await modalActions.executePendingAction();
          onClose();
        }, 500);
      }
    },
    onError: error => {
      if (
        error.message.includes('UNVERIFIED_USER') ||
        error.message.includes('User is not confirmed')
      ) {
        // Show the page and automatically send first verification email (only once)
        setShowResendConfirmationCodePage(true);
        // Only send if not already sent and not currently sending
        if (!hasAutoSentRef.current && !isSendingRef.current && !resendConfirmationCodeLoading) {
          hasAutoSentRef.current = true;
          isSendingRef.current = true;
          resendConfirmationCode({ variables: { email } });
        }
      } else if (error.message.includes('OAUTH_ONLY_USER')) {
        // Extract provider information from error message
        const message = error.message.replace('OAUTH_ONLY_USER: ', '');
        toast.error(message, {
          autoClose: 15000,
          closeButton: true,
        });
      } else if (error.message.includes('REGISTERED_USER_AUTH_FAILED')) {
        // User is registered but authentication failed - suggest OAuth or forgot password
        const message = error.message.replace('REGISTERED_USER_AUTH_FAILED: ', '');
        toast.error(message, {
          autoClose: 15000,
          closeButton: true,
        });
      } else {
        toast.error(error.message, { autoClose: 10000 });
      }
    },
  });

  const handleSubmit = () => {
    const validation = validateSignInForm({ email, password });
    setEmailError(validation.emailError);
    setPasswordError(validation.passwordError);
    if (!validation.ok) {
      if (validation.toast) {
        toast.error(validation.toast);
      }
      return;
    }

    signIn({
      variables: {
        input: { email: email.trim(), password },
      },
    });
  };

  const onBackToLogin = () => {
    setShowResendConfirmationCodePage(false);
    setResendCooldown(0); // Reset countdown when going back
    hasAutoSentRef.current = false; // Reset auto-sent flag when going back
  };

  // Countdown timer effect
  useEffect(() => {
    if (resendCooldown > 0) {
      countdownIntervalRef.current = setInterval(() => {
        setResendCooldown(prev => {
          if (prev <= 1) {
            if (countdownIntervalRef.current) {
              clearInterval(countdownIntervalRef.current);
              countdownIntervalRef.current = null;
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    }

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, [resendCooldown]);

  const handleResendVerificationCode = () => {
    if (resendCooldown > 0 || resendConfirmationCodeLoading || isSendingRef.current) {
      return; // Still in cooldown, loading, or already sending
    }
    isSendingRef.current = true;
    resendConfirmationCode({ variables: { email } });
  };

  const handleCloseAndResetState = () => {
    setShowResendConfirmationCodePage(false);
    modalActions.closeSignInModal();
  };

  const handleSwitchToSignUp = () => {
    modalActions.switchToSignUp();
  };

  const renderLoginForm = () => (
    <Container data-testid="signin-modal-container">
      <Title>Log in with email</Title>

      <FormGroup>
        <Label>Email</Label>
        <Input
          type="email"
          placeholder="Email"
          value={email}
          onChange={e => {
            setEmail(e.target.value);
            setEmailError(undefined);
          }}
          data-testid="signin-email-input"
          $invalid={!!emailError}
          aria-invalid={!!emailError}
        />
        {emailError && <FieldError>{emailError}</FieldError>}
      </FormGroup>

      <FormGroup>
        <Label>Password</Label>
        <Input
          type="password"
          placeholder="Password"
          value={password}
          onChange={e => {
            setPassword(e.target.value);
            setPasswordError(undefined);
          }}
          data-testid="signin-password-input"
          $invalid={!!passwordError}
          aria-invalid={!!passwordError}
        />
        {passwordError && <FieldError>{passwordError}</FieldError>}
      </FormGroup>

      <ForgotPasswordContainer>
        <Link
          onClick={() => {
            modalActions.openForgotPwdModal();
            modalActions.setCurrentEmail(email);
          }}
          data-testid="signin-forgot-password-button"
        >
          Forgot password?
        </Link>
        <Link onClick={handleSwitchToSignUp} data-testid="signin-signup-link">
          No Account? Sign Up
        </Link>
      </ForgotPasswordContainer>

      <Button
        text="Continue"
        onClick={handleSubmit}
        isDisabled={loading}
        isLoading={loading}
        dataTestId="signin-submit-button"
      />

      {showGoogleSignIn() && (
        <>
          <DividerContainer>
            <DividerLine />
            <DividerText>or</DividerText>
            <DividerLine />
          </DividerContainer>

          <GoogleSignInButton title="Sign in with Google" dataTestId="signin-google-button" />
          <Footer>By continuing you agree to the Terms and Privacy Policy</Footer>
        </>
      )}

      <Footer>
        By logging in, you agree to {getBrand().legalName}'s <Link>terms of service</Link> and{' '}
        <Link>privacy policy</Link>
      </Footer>
    </Container>
  );

  const renderByCase = () => {
    if (showResendConfirmationCodePage) {
      return (
        <ResendConfirmationCodeContainer>
          <ResendConfirmationCodeTitle>Please check your Email Inbox</ResendConfirmationCodeTitle>
          <p>We've just sent a confirmation Link to your email.</p>
          <ResendConfirmationCodeImage src={verifyEmail} alt="verify-email" />

          <ResendConfirmationCodeContent>
            <TipBox>
              <FontAwesomeIcon icon={faInfoCircle} tw="text-black mt-1" />
              There may be a delay in receiving your email.
            </TipBox>
            <TipBox>
              <FontAwesomeIcon icon={faInfoCircle} tw="text-black mt-1" />
              If you didn't receive it, please check your spam folder.
            </TipBox>
            <ButtonContainer>
              <Button text="Confirmed, Back to Login" onClick={onBackToLogin} buttonWidth="200px" />
            </ButtonContainer>
            <ButtonContainer>
              <Button
                text={
                  resendCooldown > 0
                    ? `Resend Verification Code (${resendCooldown})`
                    : 'Resend Verification Code'
                }
                onClick={handleResendVerificationCode}
                buttonWidth="250px"
                isDisabled={resendCooldown > 0 || resendConfirmationCodeLoading}
                isLoading={resendConfirmationCodeLoading}
              />
            </ButtonContainer>
          </ResendConfirmationCodeContent>
        </ResendConfirmationCodeContainer>
      );
    }
    return renderLoginForm();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleCloseAndResetState}>
      {renderByCase()}
      <AppVersionLabel variant="modal" />
    </Modal>
  );
};

export default SignInModal;

const ResendConfirmationCodeImage = styled.img`
  ${tw`w-full h-[300px] object-cover`}
`;

const ResendConfirmationCodeContainer = styled.div`
  ${tw`flex flex-col  gap-4 min-h-[300px]`}
`;

const ResendConfirmationCodeTitle = styled.b`
  ${tw`text-xl font-bold mb-2`}
`;

const ResendConfirmationCodeContent = styled.div`
  ${tw`flex flex-col  gap-4`}
`;

const ButtonContainer = styled.div`
  ${tw`mt-6 w-full flex justify-center`}
`;
